import { LanguageSwitcher } from '@/components/shared'
import { MULTI_LOCALE } from '@/i18n'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { formatAddress } from '@/utils/format'
import { useWalletAuth } from '@/hooks/useWalletAuth'
import { useUserStore } from '@/stores/user'
import logoImg from '@/assets/logo.jpg'
import { useTranslation } from 'react-i18next'

interface HeaderProps {
  notificationCount?: number
}

export default function Header({
  notificationCount = 0,
}: HeaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { address } = useAccount()
  const isAuthenticated = useUserStore(s => s.isAuthenticated)
  const { authenticate, disconnect, isConnecting, error: walletError, clearError } = useWalletAuth()
  const [showDisconnect, setShowDisconnect] = useState(false)
  const [showLanguage, setShowLanguage] = useState(false)


  useEffect(() => {
    if (!walletError) return
    const timer = setTimeout(clearError, 5000)
    return () => clearTimeout(timer)
  }, [walletError, clearError])

  const handleWalletClick = () => {
    if (isAuthenticated && address) {
      setShowDisconnect((v) => !v)
    } else {
      authenticate()
    }
  }

  return (
    <>
      <header
        className="fixed top-0 z-50 h-14 w-full flex items-center justify-between px-4"
        style={{
          maxWidth: 430,
          background: 'rgba(7,6,11,0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(46,107,255,0.15)',
          boxShadow: '0 1px 8px rgba(46,107,255,0.1)',
        }}
      >
        {/* Logo */}
        <a href="/" className="flex items-center no-underline" style={{ textDecoration: 'none' }}>
          <img src={logoImg} alt="MC ALLIANCE" style={{ height: 32, width: 'auto', borderRadius: 4 }} />
        </a>

        {/* Right actions */}
        <div className="flex items-center gap-3">

          {/* 语言切换：只配置一门语言时 MULTI_LOCALE 为 false，整个入口不渲染 */}
          {MULTI_LOCALE && (
            <button
              onClick={() => setShowLanguage(true)}
              className="flex items-center justify-center w-9 h-9 rounded-lg"
              style={{
                minHeight: 0,
                background: 'rgba(46,107,255,0.08)',
                border: '1px solid rgba(46,107,255,0.15)',
              }}
              aria-label={t('header.language')}
            >
              <span className="material-icons" style={{ color: '#5B8CFF', fontSize: 18 }}>language</span>
            </button>
          )}

          {/* Notification Bell */}
          <button
            onClick={() => navigate('/notifications')}
            className="relative flex items-center justify-center w-9 h-9 rounded-lg"
            style={{
              minHeight: 0,
              background: 'rgba(46,107,255,0.08)',
              border: '1px solid rgba(46,107,255,0.15)',
            }}
            aria-label={t('header.notification')}
          >
            <span className="material-icons" style={{ color: '#5B8CFF', fontSize: 18 }}>notifications</span>
            {notificationCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center font-bold"
                style={{
                  background: '#2E6BFF',
                  color: '#FFFFFF',
                  fontSize: 9,

                }}
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>

          {/* Wallet button */}
          {isAuthenticated && address ? (
            <div className="relative">
              <button
                onClick={handleWalletClick}
                className="flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-medium"
                style={{
                  minHeight: 0,
                  background: 'rgba(46,107,255,0.12)',
                  border: '1px solid rgba(46,107,255,0.3)',
                  color: '#5B8CFF',

                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#4CAF8A' }} />
                {formatAddress(address)}
              </button>
              {showDisconnect && (
                <button
                  onClick={() => { disconnect(); setShowDisconnect(false) }}
                  className="absolute right-0 top-11 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap"
                  style={{
                    background: '#0C0C1C',
                    border: '1px solid rgba(46,107,255,0.3)',
                    color: '#5B8CFF',

                  }}
                >
                  {t('header.disconnect')}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleWalletClick}
              disabled={isConnecting}
              className="flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-semibold"
              style={{
                minHeight: 0,
                background: 'linear-gradient(135deg, #2E6BFF, #1E4ACC)',
                color: '#FFFFFF',

                opacity: isConnecting ? 0.6 : 1,
                boxShadow: '0 0 30px rgba(46,107,255,0.3), 0 4px 20px rgba(46,107,255,0.25)',
              }}
            >
              <span className="material-icons text-sm" style={{ fontSize: 16 }}>account_balance_wallet</span>
              {isConnecting ? t('header.connecting') : t('header.connectWallet')}
            </button>
          )}
        </div>
      </header>
      <LanguageSwitcher open={showLanguage} onClose={() => setShowLanguage(false)} />

      {walletError && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl text-xs font-medium flex items-center gap-2.5"
          style={{
            maxWidth: 380,
            background: 'rgba(15,13,24,0.95)',
            border: '1px solid rgba(46,107,255,0.3)',
            color: '#E2D6FF',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 20px rgba(46,107,255,0.1)',
          }}
        >
          <span className="material-icons" style={{ color: '#5B8CFF', fontSize: 16 }}>info</span>
          <span className="flex-1">{walletError}</span>
          <button
            onClick={clearError}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full"
            style={{ background: 'rgba(46,107,255,0.15)', color: '#5B8CFF', lineHeight: 1, fontSize: 13 }}
          >
            ×
          </button>
        </div>
      )}

    </>
  )
}
